export function safeCall(fn, fallback) {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
}

export async function safeCallAsync(fn, fallback) {
  try {
    return await fn();
  } catch (e) {
    return fallback;
  }
}

export function emptySnapshot() {
  return { fields: [], autocompletes: [], dates: [], reactSelects: [], tinymce: {} };
}

const SKIP_TYPES = new Set(["password", "file", "hidden", "submit", "button", "reset", "image"]);
const AC_ORDER = ["Continent", "Country", "State", "City", "Area", "Nationality"];
const REVEAL_SNIPPETS = [
  ["Address Line 2", "Add Address Line 2"],
  ["Tel. 2", "Add Tel 2"],
  ["Tel 2", "Add Tel 2"],
  ["Tel. 3", "Add Tel 3"],
  ["Tel 3", "Add Tel 3"],
];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function fieldType(el) {
  return ((el && (el.type || el.tagName)) || "text").toLowerCase();
}

export function isAutocompleteInput(el) {
  return !!(
    el &&
    (el.classList?.contains("MuiAutocomplete-input") ||
      el.closest?.(".MuiAutocomplete-root") ||
      el.getAttribute?.("aria-autocomplete") === "list")
  );
}

function isPhoneChrome(el) {
  return !!(el && el.closest?.(".country-list, .flag-dropdown, .react-tel-input .search-box"));
}

function isDatePickerPart(el) {
  return !!(el && el.closest?.(".react-date-picker"));
}

export function normalizeLabel(text) {
  return (text || "").replace(/\s+/g, " ").trim().replace(/[:\s]+$/, "").trim();
}

function cssEscape(id) {
  if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(id);
  return String(id).replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}

function labelFromNode(node) {
  if (!node) return "";
  if (node.tagName === "LABEL" || node.classList?.contains("form-label") || node.classList?.contains("col-form-label")) {
    return normalizeLabel(node.textContent);
  }
  const lab = node.querySelector?.("label, .form-label, .col-form-label");
  if (lab && !node.querySelector?.("input, textarea, select, .MuiAutocomplete-root, .react-date-picker")) {
    return normalizeLabel(lab.textContent);
  }
  return "";
}

function geometricLabel(el, modal) {
  return safeCall(() => {
    const labels = [...modal.querySelectorAll("label, .form-label, .col-form-label")];
    const er = el.getBoundingClientRect();
    if (!er.width && !er.height) return "";
    let bestLeft = null;
    let bestLeftDist = Infinity;
    let bestAbove = null;
    let bestAboveDist = Infinity;
    for (const lab of labels) {
      if (lab.contains(el)) continue;
      const lr = lab.getBoundingClientRect();
      if (!lr.width && !lr.height) continue;
      const vOverlap = Math.min(er.bottom, lr.bottom) - Math.max(er.top, lr.top);
      const hOverlap = Math.min(er.right, lr.right) - Math.max(er.left, lr.left);
      const leftOf = lr.right <= er.left + 16;
      const above = lr.bottom <= er.top + 16;
      if (leftOf && vOverlap > 4) {
        const dist = er.left - lr.right;
        if (dist < bestLeftDist) {
          bestLeftDist = dist;
          bestLeft = lab;
        }
      }
      if (above && hOverlap > 8) {
        const dist = er.top - lr.bottom;
        if (dist < bestAboveDist) {
          bestAboveDist = dist;
          bestAbove = lab;
        }
      }
    }
    if (bestLeft) return normalizeLabel(bestLeft.textContent);
    if (bestAbove) return normalizeLabel(bestAbove.textContent);
    return "";
  }, "");
}

export function associatedLabel(el, modal) {
  return safeCall(() => {
    if (!el) return "";

    const check = el.closest?.(".form-check, .ui.checkbox, .checkbox");
    if (check) {
      const lab = check.querySelector("label");
      if (lab) return normalizeLabel(lab.textContent);
    }

    if (el.id && modal) {
      const lab = modal.querySelector(`label[for="${cssEscape(el.id)}"]`);
      if (lab) return normalizeLabel(lab.textContent);
    }

    const mui = el.closest?.(".MuiFormControl-root");
    if (mui) {
      const lab = mui.querySelector("label");
      const t = lab && normalizeLabel(lab.textContent);
      if (t) return t;
    }

    let node =
      el.closest?.(
        ".react-tel-input, .react-phone-input-2, .PhoneInput, .react-date-picker, .MuiAutocomplete-root, .react-datepicker-wrapper"
      ) || el;
    for (let depth = 0; depth < 16 && node && node !== modal; depth++) {
      let prev = node.previousElementSibling;
      while (prev) {
        const text = labelFromNode(prev);
        if (text) return text;
        prev = prev.previousElementSibling;
      }
      node = node.parentElement;
    }

    return geometricLabel(el, modal) || normalizeLabel(el.getAttribute?.("aria-label") || el.name || "");
  }, "");
}

export function allControls(modal) {
  return safeCall(() => {
    if (!modal || !modal.querySelectorAll) return [];
    return [...modal.querySelectorAll("input, textarea, select")].filter((el) => {
      const type = (el.type || "").toLowerCase();
      if (SKIP_TYPES.has(type)) return false;
      if (isPhoneChrome(el)) return false;
      if (isDatePickerPart(el)) return false;
      if (el.closest?.(".gp-draft-banner")) return false;
      if (el.closest?.(".MuiAutocomplete-popper")) return false;
      return true;
    });
  }, []);
}

export function fieldKey(el, modal) {
  return safeCall(() => {
    const type = fieldType(el);
    const label = associatedLabel(el, modal);
    const placeholder = el.placeholder || "";
    const same = allControls(modal).filter((other) => {
      return (
        fieldType(other) === type &&
        associatedLabel(other, modal) === label &&
        (other.placeholder || "") === placeholder
      );
    });
    return `${type}::${label}::${placeholder}#${Math.max(0, same.indexOf(el))}`;
  }, "unknown");
}

export function setNativeValue(element, value) {
  safeCall(() => {
    if (!element) return;
    const proto =
      element.tagName === "SELECT"
        ? window.HTMLSelectElement.prototype
        : element.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    const prev = element.value;
    if (element._valueTracker) element._valueTracker.setValue(prev == null ? "" : String(prev));
    if (setter) setter.call(element, value);
    else element.value = value;
    element.dispatchEvent(new Event("input", { bubbles: true }));
    element.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

export function collectSnapshot(modal) {
  return safeCall(() => {
    if (!modal) return emptySnapshot();
    const fields = [];
    allControls(modal).forEach((el, order) => {
      if (isAutocompleteInput(el)) return;
      const type = fieldType(el);
      let value = el.value;
      if (el.placeholder && String(value) === String(el.placeholder)) value = "";
      fields.push({
        key: fieldKey(el, modal),
        type,
        label: associatedLabel(el, modal),
        placeholder: el.placeholder || "",
        name: el.name || "",
        order,
        value,
        checked: !!el.checked,
      });
    });

    const autocompletes = [];
    [...(modal.querySelectorAll?.(".MuiAutocomplete-root") || [])].forEach((root, index) => {
      const input = root.querySelector("input");
      if (!input) return;
      const value = (input.value || "").trim();
      const label = associatedLabel(input, modal);
      if (!value) return;
      if (label && value.toLowerCase() === label.toLowerCase()) return;
      autocompletes.push({ key: label || `ac:${index}`, label, value, index });
    });

    const dates = [...(modal.querySelectorAll?.(".react-date-picker") || [])].map((root, index) => {
      const inputs = [...root.querySelectorAll("input")].filter((i) => (i.type || "") !== "hidden");
      return {
        index,
        label: associatedLabel(inputs[0] || root, modal),
        values: inputs.map((i) => i.value),
      };
    });

    const reactSelects = [...(modal.querySelectorAll?.('input[id^="react-select"]') || [])]
      .map((input, index) => {
        const control =
          input.closest('[class*="control"]') || input.closest('[class*="react-select"]') || input.parentElement;
        const values = [
          ...(control
            ? control.querySelectorAll('[class*="multi-value__label"], [class*="singleValue"], [class*="single-value"]')
            : []),
        ].map((n) => normalizeLabel(n.textContent));
        return {
          index,
          label: associatedLabel(input, modal),
          values: values.filter(Boolean),
          text: (input.value || "").trim(),
        };
      })
      .filter((s) => s.values.length || s.text);

    const tinymce = {};
    if (window.tinymce && window.tinymce.editors) {
      window.tinymce.editors.forEach((ed) => {
        try {
          if (ed && ed.id && modal.querySelector(`#${cssEscape(ed.id)}, [id="${cssEscape(ed.id)}"]`)) {
            tinymce[ed.id] = ed.getContent();
          }
        } catch (e) {}
      });
    }

    return { fields, autocompletes, dates, reactSelects, tinymce };
  }, emptySnapshot());
}

export function findField(modal, f) {
  return safeCall(() => {
    if (!modal || !f) return null;
    const els = allControls(modal).filter((el) => !isAutocompleteInput(el));
    if (f.key) {
      const exact = els.find((el) => fieldKey(el, modal) === f.key);
      if (exact) return exact;
    }
    const type = (f.type || "text").toLowerCase();
    const typed = els.filter((el) => fieldType(el) === type);
    if (f.label) {
      const byLabel = typed.filter((el) => associatedLabel(el, modal) === f.label);
      if (byLabel.length === 1) return byLabel[0];
      if (byLabel.length > 1 && f.placeholder) {
        const byPh = byLabel.filter((el) => (el.placeholder || "") === (f.placeholder || ""));
        if (byPh.length === 1) return byPh[0];
      }
      if (byLabel[0]) return byLabel[0];
    }
    if (f.placeholder) {
      const byPh = typed.filter((el) => (el.placeholder || "") === f.placeholder);
      if (byPh.length === 1) return byPh[0];
    }
    if (typeof f.order === "number" && els[f.order] && fieldType(els[f.order]) === type) {
      return els[f.order];
    }
    return null;
  }, null);
}

export function restoreFields(modal, fields, toggledChecks) {
  safeCall(() => {
    (fields || []).forEach((f) => {
      safeCall(() => {
        const el = findField(modal, f);
        if (!el || el.disabled) return;
        if (f.type === "checkbox" || f.type === "radio") {
          if (!!el.checked === !!f.checked) return;
          const id = f.label || f.key;
          if (id && toggledChecks && toggledChecks.has(id)) return;
          el.click();
          if (!!el.checked !== !!f.checked) {
            const lab = el.closest(".form-check, .ui.checkbox, .checkbox")?.querySelector("label");
            if (lab) lab.click();
          }
          if (id && toggledChecks) toggledChecks.add(id);
          return;
        }
        const next = f.value ?? "";
        if (!String(next).trim() && String(el.value || "").trim()) return;
        if (el.placeholder && String(next) === String(el.placeholder)) return;
        if (String(el.value) !== String(next)) {
          setNativeValue(el, next);
        }
      });
    });
  });
}

export function restoreDatePickers(modal, items) {
  safeCall(() => {
    (items || []).forEach((item) => {
      const roots = [...(modal.querySelectorAll?.(".react-date-picker") || [])];
      const root = roots[item.index];
      if (!root) return;
      const inputs = [...root.querySelectorAll("input")].filter((i) => (i.type || "") !== "hidden");
      (item.values || []).forEach((val, i) => {
        if (inputs[i] && val != null && String(inputs[i].value) !== String(val)) {
          setNativeValue(inputs[i], val);
        }
      });
    });
  });
}

export async function restoreReactSelects(modal, items) {
  await safeCallAsync(async () => {
    for (const item of items || []) {
      const inputs = [...(modal.querySelectorAll?.('input[id^="react-select"]') || [])];
      const input = inputs[item.index];
      if (!input || input.disabled) continue;
      const want = (item.values && item.values[0]) || item.text;
      if (!want) continue;
      input.focus();
      setNativeValue(input, want);
      await sleep(180);
      const options = [
        ...document.querySelectorAll('[class*="react-select__option"], [id*="react-select"][id*="-option-"]'),
      ];
      const match = options.find((o) => normalizeLabel(o.textContent).toLowerCase() === want.toLowerCase());
      if (match) match.click();
      else {
        input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true, cancelable: true }));
      }
      await sleep(80);
    }
  });
}

function findAutocompleteInput(modal, item) {
  return safeCall(() => {
    const roots = [...(modal.querySelectorAll?.(".MuiAutocomplete-root") || [])];
    const want = normalizeLabel(item.label || "");
    if (want) {
      for (const root of roots) {
        const input = root.querySelector("input");
        if (associatedLabel(input, modal) === want) return input;
      }
    }
    if (typeof item.index === "number" && roots[item.index]) {
      return roots[item.index].querySelector("input");
    }
    if (roots.length === 1) return roots[0].querySelector("input");
    return null;
  }, null);
}

function optionMatches(optionText, want) {
  const text = normalizeLabel(optionText).toLowerCase();
  const target = String(want || "").toLowerCase();
  if (!target) return false;
  if (text === target) return true;
  return text.split(" - ")[0].trim() === target;
}

export async function restoreAutocompletes(modal, items) {
  await safeCallAsync(async () => {
    const list = [...(items || [])].sort((a, b) => {
      const ia = AC_ORDER.indexOf(normalizeLabel(a.label));
      const ib = AC_ORDER.indexOf(normalizeLabel(b.label));
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

    for (const item of list) {
      const value = (item.value || "").trim();
      if (!value) continue;
      if (item.label && value.toLowerCase() === String(item.label).toLowerCase()) continue;
      const input = findAutocompleteInput(modal, item);
      if (!input) continue;
      const current = (input.value || "").trim();
      if (current.toLowerCase() === value.toLowerCase()) continue;

      const start = Date.now();
      while (input.disabled && Date.now() - start < 3500) {
        await sleep(150);
      }
      if (input.disabled) continue;

      input.focus();
      setNativeValue(input, value);
      input.dispatchEvent(new KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true, cancelable: true }));
      await sleep(250);
      const options = [
        ...document.querySelectorAll('.MuiAutocomplete-popper [role="option"], [role="listbox"] [role="option"]'),
      ];
      const match = options.find((o) => optionMatches(o.textContent, value));
      if (match) {
        match.click();
      } else {
        setNativeValue(input, current);
        input.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
        input.blur();
      }
      await sleep(140);
    }
  });
}

export function restoreTiny(modal, tinymce) {
  safeCall(() => {
    if (!tinymce || !window.tinymce) return;
    Object.keys(tinymce).forEach((id) => {
      try {
        const ed = window.tinymce.get(id);
        if (ed) ed.setContent(tinymce[id] || "");
      } catch (e) {}
    });
  });
}

function clickReveal(modal, snippet) {
  return safeCall(() => {
    const icons = [...(modal.querySelectorAll?.(".plus-icon") || [])];
    for (const icon of icons) {
      const host = icon.parentElement;
      if (host && (host.textContent || "").replace(/\s+/g, " ").includes(snippet)) {
        icon.dispatchEvent(new MouseEvent("click", { bubbles: true }));
        return true;
      }
    }
    return false;
  }, false);
}

export function revealOptionalFields(modal, fields) {
  safeCall(() => {
    const wanted = new Set((fields || []).map((f) => f.label).filter(Boolean));
    REVEAL_SNIPPETS.forEach(([label, snippet]) => {
      if (!wanted.has(label)) return;
      if (allControls(modal).some((el) => associatedLabel(el, modal) === label)) return;
      clickReveal(modal, snippet);
    });

    const paymentLabels = ["Company", "IBAN", "SWIFT", "Swift", "Account Number", "Sort Code", "Currency"];
    const needsPayment = (fields || []).some((f) => paymentLabels.includes(f.label) && String(f.value || "").trim());
    if (needsPayment) {
      const boxes = [...(modal.querySelectorAll?.(".ui.checkbox, .checkbox, .form-check") || [])];
      const pay = boxes.find((b) => /Add Payment Details/i.test(b.textContent || ""));
      const input = pay && pay.querySelector("input");
      if (pay && input && !input.checked) {
        (pay.querySelector("label") || input).click();
      }
    }
  });
}

export function blankForm(modal) {
  safeCall(() => {
    allControls(modal).forEach((el) => {
      if (isAutocompleteInput(el)) return;
      const type = fieldType(el);
      if (type === "checkbox" || type === "radio") {
        if (el.checked) {
          const clickable =
            el.closest(".form-check, .ui.checkbox, .checkbox")?.querySelector("label") || el;
          clickable.click();
        }
        return;
      }
      if (el.value) setNativeValue(el, "");
    });
    modal.querySelectorAll?.(".MuiAutocomplete-root").forEach((root) => {
      const clear = root.querySelector(".MuiAutocomplete-clearIndicator");
      if (clear) clear.click();
    });
  });
}

export async function applyDraft(modal, draft, toggledChecks = new Set()) {
  await safeCallAsync(async () => {
    if (!modal || !draft) return;
    revealOptionalFields(modal, draft.fields);
    restoreFields(modal, draft.fields, toggledChecks);
    restoreDatePickers(modal, draft.dates);
    restoreTiny(modal, draft.tinymce);
    await restoreReactSelects(modal, draft.reactSelects);
    await restoreAutocompletes(modal, draft.autocompletes);
  });
}
